import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import classNames from 'classnames';

import { withStyles } from '@material-ui/core/styles';
import TableCell from '@material-ui/core/TableCell';
import TableRow from '@material-ui/core/TableRow';

import Link from 'components/common/Link';
import Tooltip from 'components/common/Tooltip';
import IconButton from 'components/common/IconButton';
import IconArrowTop from 'components/icons/IconArrowTop';
import IconArrowBottom from 'components/icons/IconArrowBottom';
import IconPen from 'components/icons/IconPen';
import IconPlus from 'components/icons/IconPlus';
import IconTrashCan from 'components/icons/IconTrashCan';
import Menu from 'components/common/Menu';
import DeleteMenu from 'components/common/DeleteMenu';
import IconTransmit from 'components/icons/IconTransmit';
import IconCash from 'components/icons/IconCash';

import formatOrderNumber from 'utils/formatOrderNumber';
import formatPrice from 'utils/formatPrice';
import formatUtcToLocalDate from 'utils/formatUtcToLocalDate';

import timeDateFormat from 'config/timeDateFormat';

import { deleteInventoryStockLine } from 'redux/modules/inventoryStockLine';

const styles = theme => ({
  root: {
    '&:last-child': {
      '& $tableCell': {
        borderBottom: 'none',
      },
    },
  },
  tableCell: {
    display: 'table-cell',
  },
  tableCellDate: {
    width: 75,
  },
  iconArrow: {
    width: 10,
    height: 10,
    marginRight: 10,
  },
  iconIncome: {
    color: theme.palette.add[500],
  },
  iconReturn: {
    color: theme.palette.notification[500],
  },
  iconSold: {
    color: theme.palette.validation[500],
  },
  button: {
    margin: -16,
    verticalAlign: 'middle',
    color: theme.palette.primary[500],
  },
  iconPen: {
    width: 16,
    height: 16,
  },
  iconPlus: {
    width: 16,
    height: 16,
    color: theme.palette.primary.A400,
  },
  iconMinus: {
    width: 16,
    height: 3,
    backgroundColor: theme.palette.primary.A400,
  },
  iconTrashCan: {
    width: 14,
    height: 18,
  },
  iconInitial: {
    display: 'inline-block',
    marginRight: 10,
    color: theme.palette.grey[700],
    fontWeight: 'bold',
  },
  iconTransmit: {
    width: 14,
    height: 14,
  },
  iconCash: {
    width: 20,
    height: 20,
  },
  tooltipRetailUsed: {
    marginLeft: 4,
    verticalAlign: 'middle',
    color: theme.palette.primary[500],
  },
});

class InventoryStockLineHistoryViewTableRow extends Component {
  constructor(props) {
    super(props);

    this.state = {
      editDialogOpen: false,
      anchorEl: null,
    };

    this.onMenuClose = this.onMenuClose.bind(this);
    this.onRemoveButtonClick = this.onRemoveButtonClick.bind(this);
    this.onRemoveCancel = this.onRemoveCancel.bind(this);
    this.onRemoveSubmit = this.onRemoveSubmit.bind(this);
  }

  onMenuClose() {
    this.setState({ anchorEl: null });
  }

  onRemoveButtonClick(event) {
    this.setState({
      anchorEl: event.currentTarget,
    });
  }

  onRemoveCancel() {
    this.setState({ anchorEl: null });
  }

  onRemoveSubmit() {
    const { actions, ID, ...otherProps } = this.props;
    actions.deleteInventoryStockLine({
      ID,
      'Product ID': otherProps['Product ID'],
    });
    this.setState({ anchorEl: null });
  }

  render() {
    const {
      ID,
      Author,
      Quantity,
      Price,
      Order,
      account,
      onEdit,
      business,
      Patient,
      Driver,
      Facility,
      Warehouse,
      classes,
      ...otherProps
    } = this.props;
    const { anchorEl } = this.state;
    const open = Boolean(anchorEl);
    const authorName = `${Author['First Name']} ${Author['Last Name']}`;
    let activity;
    let icon;
    switch (otherProps['Line Type']) {
      case 'Incoming':
        icon = (
          <IconArrowTop className={classNames(classes.iconArrow, classes.iconIncome)} />
        );
        activity = `Stock${'\xA0'}In, ${authorName}`;
        break;
      case 'Sale':
        icon = (
          <IconArrowBottom className={classNames(classes.iconArrow, classes.iconSold)} />
        );
        activity = `Sold, ${authorName}`;
        break;
      case 'Returns':
        icon = (
          <IconArrowTop className={classNames(classes.iconArrow, classes.iconReturn)} />
        );
        activity = `Return, ${authorName}`;
        break;
      case 'Initial':
        icon = (
          <div className={classes.iconInitial}>
            0
          </div>
        );
        activity = `Initial, ${authorName}`;
        break;
      default:
        activity = `${otherProps['Line Type']}, ${authorName}`;
    }
 
    return (
      <TableRow
        className={classes.root}
      >
        <TableCell
          className={classNames(classes.tableCell, classes.tableCellDate)}
        >
          {formatUtcToLocalDate(
            otherProps['Create At'],
            timeDateFormat,
            timeDateFormat,
            business['Company Address']['Time Zone'],
            business['Company Address']['Observes Daylight Saving'],
          )}
        </TableCell>
        <TableCell className={classes.tableCell}>
          {icon}
          {activity}
        </TableCell>
        <TableCell className={classes.tableCell}>
          {(Patient && Patient['Record Number']) &&
            <Link
              href={`/charts/${Patient['Record Number']}`}
              target="_blank"
            >
              {`${Patient['First Name']}, ${Patient['Last Name']}`}
            </Link>
          }
        </TableCell>
        <TableCell className={classes.tableCell}>
          {(Order && Order.ID && !Order['Is Delete']) &&
            <Link
              href={`/workflow/order/view/${Order.ID}`}
              target="_blank"
            >
              {formatOrderNumber(Order.ID)}
            </Link>
          }
          {(Order && Order.ID && Order['Is Delete']) &&
            formatOrderNumber(Order.ID)
          }
        </TableCell>
        {otherProps['Calories Per Unit'] && (
          <Fragment>
            <TableCell className={classes.tableCell}>
              {(Quantity && otherProps['Quantity In Case']) ? Math.round((Number(Quantity) * 100) / (otherProps['Quantity In Case'] * otherProps['Calories Per Unit'])) : ''}
            </TableCell>
            <TableCell className={classes.tableCell}>
              {otherProps['Quantity In Case']}
            </TableCell>
            <TableCell className={classes.tableCell}>
              {Quantity ? Math.round((Number(Quantity) * 100) / otherProps['Calories Per Unit']) : ''}
            </TableCell>
            <TableCell className={classes.tableCell}>
              {Price ? formatPrice((Price / 100) * otherProps['Calories Per Unit']) : ''}
            </TableCell>
            <TableCell
              className={classes.tableCell}
            >
              {Quantity}
            </TableCell>
            <TableCell className={classes.tableCell}>
              {Price ? formatPrice(Price) : ''}
            </TableCell>
          </Fragment>
        )}
        {(!otherProps['Calories Per Unit'] && otherProps['Quantity In Case']) && (
          <Fragment>
            <TableCell className={classes.tableCell}>
              {Math.floor(Quantity / otherProps['Quantity In Case'])}
            </TableCell>
            <TableCell className={classes.tableCell}>
              {otherProps['Quantity In Case']}
            </TableCell>
            <TableCell
              className={classes.tableCell}
            >
              {Quantity}
            </TableCell>
            <TableCell className={classes.tableCell}>
              {Price ? formatPrice(Price) : ''}
            </TableCell>
          </Fragment>
        )}
        {(!otherProps['Calories Per Unit'] && !otherProps['Quantity In Case']) && (
          <Fragment>
            <TableCell
              className={classes.tableCell}
            >
              {Quantity}
            </TableCell>
            <TableCell className={classes.tableCell}>
              {Price ? formatPrice(Price) : ''}
            </TableCell>
          </Fragment>
        )}
        <TableCell className={classes.tableCell}>
          {otherProps['Retail Price'] ? formatPrice(otherProps['Retail Price']) : ''}
          <br />
          {otherProps['Is Retail Price For Claims'] &&
            <Tooltip
              className={classes.tooltipRetailUsed}
              content="Used for Claims"
              placement="bottom"
              color="dark"
              disableGutters
            >
              <IconTransmit className={classes.iconTransmit} />
            </Tooltip>
          }
          {otherProps['Is Retail Price For Self Pay'] &&
            <Tooltip
              className={classes.tooltipRetailUsed}
              content="Used for Self Pay"
              placement="bottom"
              color="dark"
              disableGutters
            >
              <IconCash className={classes.iconCash} />
            </Tooltip>
          }
        </TableCell>
        <TableCell className={classes.tableCell}>
          {(Driver && Driver.ID) && (
            `${Driver['First Name']}, ${Driver['Last Name']}`
          )}
        </TableCell>
        <TableCell className={classes.tableCell}>
          {(Facility && Facility.ID) &&
            Facility['Company Info']['Company Name']
          }
        </TableCell>
        <TableCell className={classes.tableCell}>
          {(Warehouse && Warehouse.ID) &&
            Warehouse.Location
          }
        </TableCell>
        <TableCell className={classes.tableCell}>
          {otherProps['Expiration Date']}
        </TableCell>
        <TableCell className={classes.tableCell}>
          {otherProps['Can Change'] &&
            <IconButton
              className={classes.button}
              onClick={() => onEdit({
                ID,
                Price,
                Quantity,
                'Quantity In Case': otherProps['Quantity In Case'],
                ...Facility && Facility.ID ? {
                  'Facility ID': Facility.ID,
                  'Facility Name': Facility['Company Info']['Company Name'],
                  'Selected Facility Name': Facility['Company Info']['Company Name'],
                } : {},
                ...Warehouse && Warehouse.ID ? {
                  'Warehouse ID': Warehouse.ID,
                  'Warehouse Location': Warehouse.Location,
                  'Selected Warehouse Location': Warehouse.Location,
                } : {},
                'Expiration Date': otherProps['Expiration Date'],
                ...otherProps['Retail Price'] ? {
                  'Retail Price': formatPrice(otherProps['Retail Price']),
                } : {},
                'Is Retail Price For Claims': otherProps['Is Retail Price For Claims'],
                'Is Retail Price For Self Pay': otherProps['Is Retail Price For Self Pay'],
              })}
              disabled={account.ID !== Author.ID
                && account.Roles.filter(item =>
                  item.Name === 'Master Administrator' || item.Name === 'Administrator' || item.Name === 'Manager').length === 0}
              tooltipContent="Edit"
            >
              <IconPen className={classes.iconPen} />
            </IconButton>
          }
        </TableCell>
        <TableCell className={classes.tableCell}>
          {otherProps['Can Change'] &&
            <Fragment>
              <IconButton
                className={classes.button}
                aria-label="Remove"
                aria-owns={open ? `${ID}-stock-line-remove-menu` : null}
                aria-haspopup="true"
                onClick={this.onRemoveButtonClick}
                disabled={account.Roles.filter(item =>
                  item.Name === 'Master Administrator' || item.Name === 'Administrator' || item.Name === 'Manager').length === 0}
                tooltipContent="Delete"
              >
                <IconTrashCan className={classes.iconTrashCan} />
              </IconButton>
              <Menu
                id={`${ID}-stock-line-remove-menu`}
                anchorEl={anchorEl}
                open={open}
                onClose={this.onMenuClose}
                list={false}
              >
                <DeleteMenu
                  text="Are you sure you want to delete this stock line?"
                  onCancel={this.onRemoveCancel}
                  onSubmit={this.onRemoveSubmit}
                />
              </Menu>
            </Fragment>
          }
        </TableCell>
      </TableRow>
    );
  }
}

InventoryStockLineHistoryViewTableRow.propTypes = {
  ID: PropTypes.number.isRequired,
  'Line Type': PropTypes.string.isRequired,
  'Create At': PropTypes.string.isRequired,
  Quantity: PropTypes.number.isRequired,
  Price: PropTypes.number,
  Order: PropTypes.shape({
    ID: PropTypes.number,
    'Is Delete': PropTypes.bool,
  }),
  'Can Change': PropTypes.bool,
  Author: PropTypes.shape({
    'First Name': PropTypes.string.isRequired,
    'Last Name': PropTypes.string.isRequired,
  }).isRequired,
  actions: PropTypes.shape({
    deleteInventoryStockLine: PropTypes.func.isRequired,
  }).isRequired,
  account: PropTypes.shape({
    Roles: PropTypes.arrayOf(PropTypes.shape({
      Name: PropTypes.string.isRequired,
    })).isRequired,
  }).isRequired,
  onEdit: PropTypes.func,
  business: PropTypes.shape({
    ID: PropTypes.number,
  }),
  Patient: PropTypes.shape({
    'Record Number': PropTypes.number,
    'First Name': PropTypes.string,
    'Last Name': PropTypes.string,
  }),
  Driver: PropTypes.shape({
    ID: PropTypes.number,
    'First Name': PropTypes.string,
    'Last Name': PropTypes.string,
  }),
  Facility: PropTypes.shape({
    ID: PropTypes.number,
  }),
  classes: PropTypes.shape({
    root: PropTypes.string.isRequired,
    iconIncome: PropTypes.string.isRequired,
    iconSold: PropTypes.string.isRequired,
    iconReturn: PropTypes.string.isRequired,
    button: PropTypes.string.isRequired,
    iconPen: PropTypes.string.isRequired,
    iconTrashCan: PropTypes.string.isRequired,
  }).isRequired,
};

InventoryStockLineHistoryViewTableRow.defaultProps = {
  Price: null,
  Order: null,
  onEdit: null,
  'Can Change': false,
  business: {},
  Patient: null,
  Driver: null,
  Facility: null,
};

const mapStateToProps = store => ({
  account: store.account,
});

const mapDispatchToProps = dispatch => ({
  actions: bindActionCreators({
    deleteInventoryStockLine,
  }, dispatch),
});

export default connect(mapStateToProps, mapDispatchToProps)(
  withStyles(styles)(InventoryStockLineHistoryViewTableRow),
);



// WEBPACK FOOTER //
// ./src/components/inventory/InventoryStockLineHistoryViewDialog/InventoryStockLineHistoryViewTableRow.js